import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContext';

const LoginModal = ({ onClose, onSwitchToSignup }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { setUser } = useContext(UserContext);

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      onClose();
    } catch (error) {
      console.error("Error during Google sign-in: ", error);
      if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup, no need to show error
        return;
      }
      setError('Failed to sign in with Google. Please try again.');
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(userCredential.user);
      onClose();
    } catch (error) {
      console.error("Error during email/password sign-in: ", error);
      
      // Handle specific errors
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('Invalid email or password');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'auth/too-many-requests':
          setError('Too many unsuccessful login attempts. Please try again later.');
          break;
        default:
          setError('An error occurred. Please try again later.');
      }
    }
  };

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" component="h2">
          Welcome Back
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Log in to access your saved retirement scenarios
        </Typography>
        
        {/* Google Sign-in Button */}
        <Button 
          variant="outlined"
          fullWidth 
          onClick={handleGoogleSignIn}
          sx={{ 
            py: 1.5, 
            mb: 2,
            justifyContent: 'flex-start',
            gap: 2
          }}
        >
          <img 
            src="https://developers.google.com/identity/images/g-logo.png" 
            alt="Google" 
            style={{ width: 20, height: 20 }} 
          />
          <span>Continue with Google</span>
        </Button>
        
        <Box sx={{ position: 'relative', my: 3 }}>
          <Divider />
          <Typography 
            variant="body2" 
            sx={{ 
              position: 'absolute',
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'background.paper',
              px: 2
            }}
          >
            or
          </Typography>
        </Box>
        
        {/* Email/Password Form */}
        <form onSubmit={handleEmailSignIn}>
          {error && (
            <Typography 
              color="error" 
              variant="body2" 
              sx={{ mb: 2 }}
            >
              {error}
            </Typography>
          )}
          
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2, py: 1.5 }}
          >
            Log In
          </Button>
        </form>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Typography variant="body2">
          New to FERS Navigator?{' '}
          <Box 
            component="span"
            onClick={onSwitchToSignup}
            sx={{ 
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Create an account
          </Box>
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default LoginModal;
